import { QSingle } from '@educraft/base';
import { IExercise, ExerciseData, addResult } from '../exercise';
import { PdfUnit, PdfRect, PdfPoint, PdfBrush, ETextAlign } from '../../pdf/pdf';
import { GUtil } from '../../pdf/gutil';
import { makeCombinedSingle, generateUnique } from './exeUtil';

interface BelowInfo {
  supported: boolean;
  width: number;
  lines: string[];
}

export class ExerciseEqBelow implements IExercise {
  readonly name = 'ExerciseEqBelow';
  readonly fixedHeight = 0;
  readonly noWindow = false;
  readonly injectEq = true;
  readonly injectAbc = false;

  private marginColumn = PdfUnit.FromMillimeters(3);
  private lineHeight = PdfUnit.FromMillimeters(8);

  draw(data: ExerciseData): void {
    if (data.item.selEq.length === 0) return;

    const gen = makeCombinedSingle(data);

    // Sample to measure sizes
    let width1 = PdfUnit.FromMillimeters(30);
    let height1 = this.lineHeight;
    const samples: QSingle[] = [];

    for (let i = 0; i < 20; i++) {
      const q = gen.generate(data.data);
      samples.push(q);
      const info = this.parseBelow(data, q);
      width1 = Math.max(width1, info.width);
      height1 = Math.max(height1, info.lines.length * this.lineHeight);
    }

    const countX = Math.max(1, Math.floor(data.rect.width / (width1 - this.marginColumn)));
    const countY = Math.max(1, Math.floor(data.rect.height / height1 / 2));
    const total = countX * countY;

    const array = generateUnique(
      gen,
      data.data,
      total,
      (q) => `${q.texts[0]}|${q.answer}`,
      samples
    );

    const cellW = (data.rect.width + this.marginColumn) / countX;
    const cellH = data.rect.height / countY;

    for (let x = 0; x < countX; x++) {
      for (let y = 0; y < countY; y++) {
        const index = y + x * countY;
        if (index >= array.length) break;
        const item = array[index];

        addResult(data, item.texts[0], item.answer);

        const r = new PdfRect(
          data.rect.x + x * cellW,
          data.rect.y + y * cellH,
          cellW - this.marginColumn,
          cellH
        );

        const info = this.parseBelow(data, item);

        if (info.supported) {
          // Right-aligned lines with horizontal separator
          for (let i = 0; i < info.lines.length; i++) {
            const r1 = new PdfRect(r.x, r.y + i * this.lineHeight, r.width, this.lineHeight);
            data.page.drawString(
              info.lines[i],
              GUtil.exerciseFont,
              PdfBrush.Black,
              null,
              r1,
              ETextAlign.CenterRight
            );
          }

          const p1 = new PdfPoint(r.right - info.width, r.y + this.lineHeight * info.lines.length);
          const p2 = new PdfPoint(r.right, r.y + this.lineHeight * info.lines.length);
          data.page.drawLine(GUtil.basePen, p1, p2);
        } else {
          // Left-aligned fallback
          for (let i = 0; i < info.lines.length; i++) {
            const r1 = new PdfRect(r.x, r.y + i * this.lineHeight, r.width, this.lineHeight);
            data.page.drawString(
              info.lines[i],
              GUtil.exerciseFont,
              PdfBrush.Black,
              null,
              r1,
              ETextAlign.CenterLeft
            );
          }
        }
      }
    }
  }

  private parseExpression(input: string): string[] {
    input = input.trim();
    if (input.endsWith('=')) input = input.slice(0, -1);

    const result: string[] = [];
    const regex = /([+\-*/\u00d7\u00f7:\u00b7\u2013])?\s*(\d+)/g;
    let match: RegExpExecArray | null;

    while ((match = regex.exec(input)) !== null) {
      const op = match[1] || '';
      const num = match[2];
      if (!op) {
        result.push(num);
      } else {
        result.push(`${op  } ${  num}`);
      }
    }

    return result;
  }

  private parseBelow(data: ExerciseData, q: QSingle): BelowInfo {
    const opRegex = /[\d.,]+\s*[^\d=]+\s*[^=]+\s*=?/;

    if (q.texts.length === 1 && opRegex.test(q.texts[0])) {
      const lines = this.parseExpression(q.texts[0]);
      if (lines.length > 0) {
        let width = 0;
        for (const line of lines) {
          const sz = data.page.measureString(line, GUtil.exerciseFont);
          width = Math.max(width, sz.width);
        }
        return { supported: true, width, lines };
      }
    }

    let width = 0;
    for (const t of q.texts) {
      const sz = data.page.measureString(t, GUtil.exerciseFont);
      width = Math.max(width, sz.width);
    }

    return { supported: false, width, lines: q.texts };
  }
}
